import { MatterRelatedMatterContextMenu } from "./MatterRelatedMatterContextMenu";
import { VehicleCopyModeModal } from "@/components/features/matter/vehicles/VehicleCopyModeModal";
import { MatterSubItemEventLogsContextMenu } from "@/components/features/matter/event-logs/MatterSubItemEventLogsContextMenu";
import { MatterSubItemEventLogsModal } from "@/components/features/matter/event-logs/MatterSubItemEventLogsModal";
import { MatterSubItemFileReferencesContextMenu } from "@/components/features/matter/file-repo/MatterSubItemFileReferencesContextMenu";
import { MatterSubItemFileReferencesModal } from "@/components/features/matter/file-repo/MatterSubItemFileReferencesModal";
import { FileReferences } from "@/components/features/matter/file-repo/FileReferences";
import {
  MatterSubItemFileReferenceUpload,
  type MatterSubItemFileReferenceUploadHandle,
} from "@/components/features/matter/file-repo/MatterSubItemFileReferenceUpload";
import { MatterSubItemNotesContextMenu } from "@/components/features/matter/notes/MatterSubItemNotesContextMenu";
import { MatterSubItemNotesModal } from "@/components/features/matter/notes/MatterSubItemNotesModal";
import { MatterSubItemTasksContextMenu } from "@/components/features/matter/tasks/MatterSubItemTasksContextMenu";
import { MatterSubItemTasksModal } from "@/components/features/matter/tasks/MatterSubItemTasksModal";
import { MatterSubItemGenerateDocumentContextMenu } from "@/components/features/matter/context-menus/MatterSubItemGenerateDocumentContextMenu";
import { MatterSubItemCreateSignatureRequestContextMenu } from "@/components/features/matter/context-menus/MatterSubItemCreateSignatureRequestContextMenu";
import { EditContactModal } from "@/components/features/contact/AddOrEditContactModal/EditContactModal";
import SelectContactModal from "@/components/features/contact/SelectContactModal/SelectContactModal";
import {
  ActionConfirmationModal,
  ContactSearchableContextMenuSubmenu,
  ContextMenu,
  ContextMenuItem,
  ContextMenuSeparator,
  SearchableContextMenuSubmenu,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import {
  useCreateConnection,
  useDeleteConnection,
  useReplaceConnection,
} from "@/lib/data/react-query/connections/connection";
import { useDeleteMatterContactConnection } from "@/lib/data/react-query/matters/matter-contact-connection";
import {
  useDeleteMatterVehicle,
  useUpdateMatterVehicle,
} from "@/lib/data/react-query/matters/matter-vehicle";
import {
  useCopyPartyVehicleToRelatedMatters,
  useUnlinkObjectFromMatter,
} from "@/lib/data/react-query/matters/matter-related-object";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { MATTER_VEHICLE_DAMAGE_SEVERITY_OPTIONS } from "@/lib/data/static/matterStatics";
import { getRelatedMatterSelectionOptions } from "@/lib/utils/relatedMatters";
import type {
  Contact,
  Connection,
  Matter,
  MatterContactConnection,
  MatterInsurance,
} from "@/lib/types";
import { formatInsuranceTitle, formatVehicleTitle } from "@/lib/utils/format";
import {
  createVehicleContactConnectionInput,
  getVehicleContactConnection,
  getVehicleContactConnections,
  isVehicleContactConnection,
  VEHICLE_PEDESTRIAN_COMPATIBILITY_CONNECTION_TYPE,
} from "@/lib/utils/connections/vehicle-contact-connections";
import type { MatterPartySlideoverRole } from "@/lib/utils/matterPartyCoverage";
import {
  getMatterContactConnectionLinkedId,
  getMatterContactConnections,
} from "@/lib/utils/matters/matter-contact-connection-party-roles";
import { getSelectableMatterInsurances } from "@/lib/utils/matters/selectable-matter-insurances";
import {
  Building2,
  Footprints,
  PlusCircle,
  Shield,
  TriangleAlert,
  UserRound,
  UserRoundCheck,
  UsersRound,
} from "lucide-react";
import { useRef, useState, type ReactNode } from "react";
import toast from "react-hot-toast";

interface MatterVehicleContextMenuProps {
  canOpen: boolean;
  insurancePartyContactId?: string;
  matter: Matter;
  onOpen: () => void;
  partyConnection: MatterContactConnection;
  partyRole: MatterPartySlideoverRole;
  trigger: ReactNode;
  vehicle: Matter["vehicles"][number];
  vehicleParty: "client" | "defendant";
}

type VehicleSingleContactRole = "Driver" | "Registered Owner";
type VehicleMultiContactRole = "Auto Shop" | "Passenger" | "Pedestrian";
type VehicleContactSelection =
  | {
      connections: Connection[];
      role: VehicleMultiContactRole;
      selectionMode: "multiple";
    }
  | {
      connection?: Connection;
      role: VehicleSingleContactRole;
      selectionMode: "single";
    };

const vehicleContactSuccessMessages: Record<VehicleSingleContactRole, string> =
  {
    Driver: "Driver updated successfully",
    "Registered Owner": "Registered owner updated successfully",
  };

export const MatterVehicleContextMenu = ({
  canOpen,
  insurancePartyContactId,
  matter,
  onOpen,
  partyConnection,
  partyRole,
  trigger,
  vehicle,
  vehicleParty,
}: MatterVehicleContextMenuProps) => {
  const { hasActionPermission, currentUser } = useUser();
  const {
    openCommunicationModal,
    openInsuranceModal,
    openSendMatterEmailModal,
  } = useGlobalModal();
  const [isEventLogsOpen, setIsEventLogsOpen] = useState(false);
  const fileReferenceUploadRef =
    useRef<MatterSubItemFileReferenceUploadHandle>(null);
  const [isFileReferencesOpen, setIsFileReferencesOpen] = useState(false);
  const [isCreateFileReferenceOpen, setIsCreateFileReferenceOpen] =
    useState(false);
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [isTasksOpen, setIsTasksOpen] = useState(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [isUnlinkConfirmationOpen, setIsUnlinkConfirmationOpen] =
    useState(false);
  const [insuranceSearch, setInsuranceSearch] = useState("");
  const [damageSeveritySearch, setDamageSeveritySearch] = useState("");
  const [contactSelection, setContactSelection] =
    useState<VehicleContactSelection>();
  const [contactBeingEdited, setContactBeingEdited] = useState<Contact>();
  const [pendingClientVehicleMatterIds, setPendingClientVehicleMatterIds] =
    useState<string[]>([]);
  const deleteVehicle = useDeleteMatterVehicle();
  const unlinkVehicle = useUnlinkObjectFromMatter({
    objectLabel: "Vehicle",
  });
  const copyPartyVehicle = useCopyPartyVehicleToRelatedMatters();
  const updateMatterVehicle = useUpdateMatterVehicle({
    showSuccessToast: false,
  });
  const deleteMatterContactConnection = useDeleteMatterContactConnection({
    showSuccessToast: false,
    showErrorToast: true,
  });
  const createConnection = useCreateConnection();
  const replaceConnection = useReplaceConnection();
  const deleteConnection = useDeleteConnection();
  const canDeleteVehicle = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterVehicle,
    ACTION_PERMISSION_VALUES.delete,
  );
  const canUpdateContact = hasActionPermission(
    ACTION_PERMISSION_KEYS.contact,
    ACTION_PERMISSION_VALUES.update,
  );
  const canSendMatterEmail = Boolean(true);
  const canTextContact = Boolean(true);
  const vehicleTitle = formatVehicleTitle(vehicle) || "vehicle";
  const vehicleContactConnections = getMatterContactConnections(matter);
  const registeredOwner = getVehicleContactConnection(
    vehicle,
    "Registered Owner",
  );
  const driver = getVehicleContactConnection(vehicle, "Driver");
  const autoShops = getVehicleContactConnections(vehicle, "Auto Shop");
  const passengers = getVehicleContactConnections(vehicle, "Passenger");
  const legacyPedestrians = vehicleContactConnections.filter(
    (connection) =>
      getMatterContactConnectionLinkedId(connection, "vehicleId") ===
        vehicle.id && connection.label === "Pedestrian",
  );
  const pedestrians = getVehicleContactConnections(vehicle, "Pedestrian");
  const clientContactIds = new Set(
    vehicleContactConnections
      .filter((connection) => connection.label === "Client")
      .map((connection) => connection.contactId),
  );
  const isVehicleLinkedToParty = vehicle.connections.some(
    (connection) =>
      isVehicleContactConnection(connection) &&
      connection.targetObjectId === partyConnection.contactId,
  );
  const hasClientPassenger = passengers.some((passenger) =>
    clientContactIds.has(passenger.targetObjectId),
  );
  const hasClientDriver = Boolean(
    driver && clientContactIds.has(driver.targetObjectId),
  );
  const showClientVehicleRoleError = () => {
    toast.error(
      "The client must be selected as either the driver or a passenger.",
    );
  };
  const getInsuranceTitle = (insurance: MatterInsurance) =>
    formatInsuranceTitle({
      insurance,
    })?.title ?? "Unnamed insurance";
  const visibleInsurances = getSelectableMatterInsurances(matter).filter(
    (insurance) =>
      insurance.type !== "Health" &&
      getInsuranceTitle(insurance)
        .toLowerCase()
        .includes(insuranceSearch.trim().toLowerCase()),
  );
  const visibleDamageSeverities = MATTER_VEHICLE_DAMAGE_SEVERITY_OPTIONS.filter(
    (damageSeverity) =>
      damageSeverity
        .toLowerCase()
        .includes(damageSeveritySearch.trim().toLowerCase()),
  );
  const updateVehicleAttributes = async (
    attributes: Partial<Matter["vehicles"][number]>,
    successMessage: string,
  ) => {
    await updateMatterVehicle.mutateAsync({
      id: vehicle.id,
      matterId: matter.id,
      dateModified: vehicle.dateModified,
      ...attributes,
    });
    toast.success(successMessage);
  };
  const setVehicleInsurance = async (
    insurance: Pick<MatterInsurance, "id">,
  ) => {
    if (insurance.id === vehicle.insuranceId) return;

    await updateVehicleAttributes(
      {
        insuranceId: insurance.id,
      },
      "Vehicle insurance updated successfully",
    );
  };
  const openCreateInsurance = () => {
    openInsuranceModal({
      matterId: matter.id,
      editing: false,
      initialTab: "details",
      requiredInsurancePartyContactId: insurancePartyContactId,
      afterCreateFn: (newInsurance) => {
        void setVehicleInsurance(newInsurance.data);
      },
    });
  };
  const setVehicleContact = async (
    contact: Contact,
    role: VehicleSingleContactRole,
    existingConnection?: Connection,
  ) => {
    const existingContactId = existingConnection?.targetObjectId;
    if (contact.id === existingContactId) return;
    if (
      role === "Driver" &&
      clientContactIds.size > 0 &&
      !clientContactIds.has(contact.id) &&
      !hasClientPassenger
    ) {
      showClientVehicleRoleError();
      return;
    }
    if (existingConnection) {
      await replaceConnection.mutateAsync({
        connectionId: existingConnection.id,
        dateModified: existingConnection.dateModified,
        matterId: matter.id,
        operation: "replace",
        replacement: createVehicleContactConnectionInput(
          vehicle.id,
          contact.id,
          role,
        ),
      });
    } else {
      await createConnection.mutateAsync({
        matterId: matter.id,
        ...createVehicleContactConnectionInput(vehicle.id, contact.id, role),
      });
    }
    toast.success(vehicleContactSuccessMessages[role]);
  };
  const toggleVehicleContact = async (
    contact: Contact,
    role: VehicleMultiContactRole,
    connections: Connection[],
  ) => {
    const existingConnection = connections.find(
      (connection) => connection.targetObjectId === contact.id,
    );

    if (existingConnection) {
      if (
        role === "Passenger" &&
        clientContactIds.has(contact.id) &&
        !hasClientDriver &&
        !passengers.some(
          (passenger) =>
            passenger.id !== existingConnection.id &&
            clientContactIds.has(passenger.targetObjectId),
        )
      ) {
        showClientVehicleRoleError();
        return;
      }
      if (
        existingConnection.connectionType ===
        VEHICLE_PEDESTRIAN_COMPATIBILITY_CONNECTION_TYPE
      ) {
        const legacyConnection = legacyPedestrians.find(
          (connection) => connection.contactId === contact.id,
        );
        if (legacyConnection) {
          await deleteMatterContactConnection.mutateAsync({
            id: legacyConnection.id,
          });
        } else {
          await deleteConnection.mutateAsync({
            connectionId: existingConnection.id,
            matterId: matter.id,
          });
        }
      } else {
        await deleteConnection.mutateAsync({
          connectionId: existingConnection.id,
          matterId: matter.id,
        });
      }
      toast.success(`${role} removed successfully`);
      return;
    }

    await createConnection.mutateAsync({
      matterId: matter.id,
      ...createVehicleContactConnectionInput(vehicle.id, contact.id, role),
    });
    toast.success(`${role} added successfully`);
  };
  const deleteVehicleSingleContact = async (
    contact: Contact,
    role: VehicleSingleContactRole,
    connection?: Connection,
  ) => {
    const connectionContactId = connection?.targetObjectId;
    if (!connection || connectionContactId !== contact.id) return;

    await deleteConnection.mutateAsync({
      connectionId: connection.id,
      matterId: matter.id,
    });
    toast.success(`${role} removed successfully`);
  };
  const selectVehicleContact = async (contact: Contact) => {
    if (!contactSelection) return;

    if (contactSelection.selectionMode === "single") {
      await setVehicleContact(
        contact,
        contactSelection.role,
        contactSelection.connection,
      );
      return;
    }

    await toggleVehicleContact(
      contact,
      contactSelection.role,
      contactSelection.connections,
    );
  };
  const emailContact = (contact: Contact) => {
    const email =
      contact.emails.find(
        (contactEmail) =>
          contactEmail.isPrimary && Boolean(contactEmail.address?.trim()),
      )?.address ??
      contact.emails.find((contactEmail) =>
        Boolean(contactEmail.address?.trim()),
      )?.address;
    if (!email) return;

    openSendMatterEmailModal({
      matterId: matter.id,
      contactId: contact.id,
      toEmails: [email],
      templateData: { firm_name: currentUser.firm.name },
      title: "Send email",
      description: "Compose and send an email for this matter.",
    });
  };
  const textContact = (contact: Contact) => {
    openCommunicationModal({ contactId: contact.id, matterId: matter.id });
  };
  const copyValue = [
    vehicleTitle,
    vehicle.vin ? `VIN: ${vehicle.vin}` : undefined,
    vehicle.licensePlateNumber
      ? `License plate: ${vehicle.licensePlateNumber}`
      : undefined,
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <>
      <ContextMenu
        entityName="vehicle"
        open={canOpen ? { onSelect: onOpen } : false}
        copy={{
          value: copyValue,
          successLabel: "Vehicle details",
        }}
        delete={
          canDeleteVehicle
            ? {
                disabled: deleteVehicle.isPending,
                onSelect: () => {
                  setIsDeleteConfirmationOpen(true);
                },
              }
            : false
        }
        unlink={
          canDeleteVehicle
            ? {
                disabled: unlinkVehicle.isPending,
                label: "Unlink vehicle",
                onSelect: () => {
                  setIsUnlinkConfirmationOpen(true);
                },
              }
            : false
        }
        trigger={trigger}
      >
        {canOpen ? (
          <ContactSearchableContextMenuSubmenu
            matterId={matter.id}
            addLabel="Add a registered owner"
            selectedContactIds={
              registeredOwner ? [registeredOwner.targetObjectId] : []
            }
            title={
              <>
                <UserRoundCheck />
                Registered owner
              </>
            }
            onAddContact={() => {
              setContactSelection({
                role: "Registered Owner",
                selectionMode: "single",
              });
            }}
            onChangeContact={() => {
              setContactSelection({
                connection: registeredOwner,
                role: "Registered Owner",
                selectionMode: "single",
              });
            }}
            onDeleteContact={(contact) => {
              void deleteVehicleSingleContact(
                contact,
                "Registered Owner",
                registeredOwner,
              );
            }}
            onEditContact={canUpdateContact ? setContactBeingEdited : undefined}
            onEmailContact={canSendMatterEmail ? emailContact : undefined}
            onTextContact={canTextContact ? textContact : undefined}
          />
        ) : null}
        {canOpen ? (
          <ContactSearchableContextMenuSubmenu
            matterId={matter.id}
            addLabel="Add a driver"
            selectedContactIds={driver ? [driver.targetObjectId] : []}
            title={
              <>
                <UserRound />
                Driver
              </>
            }
            isRequired={Boolean(
              driver &&
                clientContactIds.has(driver.targetObjectId) &&
                !hasClientPassenger,
            )}
            onAddContact={() => {
              setContactSelection({ role: "Driver", selectionMode: "single" });
            }}
            onChangeContact={() => {
              setContactSelection({
                connection: driver,
                role: "Driver",
                selectionMode: "single",
              });
            }}
            onDeleteContact={(contact) => {
              void deleteVehicleSingleContact(contact, "Driver", driver);
            }}
            onEditContact={canUpdateContact ? setContactBeingEdited : undefined}
            onEmailContact={canSendMatterEmail ? emailContact : undefined}
            onTextContact={canTextContact ? textContact : undefined}
          />
        ) : null}
        {canOpen ? (
          <ContactSearchableContextMenuSubmenu
            matterId={matter.id}
            addLabel="Add an auto shop"
            selectionMode="multiple"
            selectedContactIds={autoShops.map(
              (autoShop) => autoShop.targetObjectId,
            )}
            title={
              <>
                <Building2 />
                Auto shops
              </>
            }
            onAddContact={() => {
              setContactSelection({
                connections: autoShops,
                role: "Auto Shop",
                selectionMode: "multiple",
              });
            }}
            onDeleteContact={(contact) => {
              void toggleVehicleContact(contact, "Auto Shop", autoShops);
            }}
            onEditContact={canUpdateContact ? setContactBeingEdited : undefined}
            onEmailContact={canSendMatterEmail ? emailContact : undefined}
            onTextContact={canTextContact ? textContact : undefined}
          />
        ) : null}
        {canOpen ? (
          <ContactSearchableContextMenuSubmenu
            matterId={matter.id}
            addLabel="Add a passenger"
            selectionMode="multiple"
            selectedContactIds={passengers.map(
              (passenger) => passenger.targetObjectId,
            )}
            title={
              <>
                <UsersRound />
                Passengers
              </>
            }
            onAddContact={() => {
              setContactSelection({
                connections: passengers,
                role: "Passenger",
                selectionMode: "multiple",
              });
            }}
            onDeleteContact={(contact) => {
              void toggleVehicleContact(contact, "Passenger", passengers);
            }}
            onEditContact={canUpdateContact ? setContactBeingEdited : undefined}
            onEmailContact={canSendMatterEmail ? emailContact : undefined}
            onTextContact={canTextContact ? textContact : undefined}
          />
        ) : null}
        {canOpen ? (
          <ContactSearchableContextMenuSubmenu
            matterId={matter.id}
            addLabel="Add a pedestrian"
            selectionMode="multiple"
            selectedContactIds={pedestrians.map(
              (pedestrian) => pedestrian.targetObjectId,
            )}
            title={
              <>
                <Footprints />
                Pedestrians
              </>
            }
            onAddContact={() => {
              setContactSelection({
                connections: pedestrians,
                role: "Pedestrian",
                selectionMode: "multiple",
              });
            }}
            onDeleteContact={(contact) => {
              void toggleVehicleContact(contact, "Pedestrian", pedestrians);
            }}
            onEditContact={canUpdateContact ? setContactBeingEdited : undefined}
            onEmailContact={canSendMatterEmail ? emailContact : undefined}
            onTextContact={canTextContact ? textContact : undefined}
          />
        ) : null}
        {canOpen ? <ContextMenuSeparator /> : null}
        {canOpen ? (
          <SearchableContextMenuSubmenu
            emptyMessage="No insurance found."
            footer={
              <ContextMenuItem
                className="hover:bg-neutral-200"
                onSelect={openCreateInsurance}
              >
                <PlusCircle />
                Create new insurance
              </ContextMenuItem>
            }
            getItemKey={(insurance) => insurance.id}
            items={visibleInsurances}
            onOpenChange={(open) => {
              if (!open) {
                setInsuranceSearch("");
              }
            }}
            onSearchChange={setInsuranceSearch}
            onSelectItem={(insurance) => {
              void setVehicleInsurance(insurance);
            }}
            placeholder="Search insurance..."
            renderItem={(insurance) => (
              <>
                <Shield />
                <span className="min-w-0 flex-1 truncate">
                  {getInsuranceTitle(insurance)}
                </span>
              </>
            )}
            search={insuranceSearch}
            selectedItemKeys={vehicle.insuranceId ? [vehicle.insuranceId] : []}
            title={
              <>
                <Shield />
                Insurance
              </>
            }
          />
        ) : null}
        {canOpen ? (
          <SearchableContextMenuSubmenu
            emptyMessage="No damage severities found."
            getItemKey={(damageSeverity) => damageSeverity}
            items={visibleDamageSeverities}
            onOpenChange={(open) => {
              if (!open) {
                setDamageSeveritySearch("");
              }
            }}
            onSearchChange={setDamageSeveritySearch}
            onSelectItem={(damageSeverity) => {
              void updateVehicleAttributes(
                { damageSeverity },
                "Vehicle damage severity updated successfully",
              );
            }}
            placeholder="Search damage severities..."
            renderItem={(damageSeverity) => <>{damageSeverity}</>}
            search={damageSeveritySearch}
            selectedItemKeys={
              vehicle.damageSeverity ? [vehicle.damageSeverity] : []
            }
            title={
              <>
                <TriangleAlert />
                Damage severity
              </>
            }
          />
        ) : null}
        {canOpen ? <ContextMenuSeparator /> : null}
        <MatterSubItemGenerateDocumentContextMenu
          matter={matter}
          subItem={{ id: vehicle.id, type: "vehicle", vehicleParty }}
        />
        <MatterSubItemCreateSignatureRequestContextMenu
          matter={matter}
          subItem={{ id: vehicle.id, type: "vehicle", vehicleParty }}
        />
        <MatterRelatedMatterContextMenu
          matter={matter}
          objectId={vehicle.id}
          objectType="vehicle"
          vehicleParty={
            isVehicleLinkedToParty
              ? { connectionId: partyConnection.id, role: partyRole }
              : undefined
          }
          onClientVehicleCopyRequested={setPendingClientVehicleMatterIds}
        />
        <MatterSubItemTasksContextMenu
          matter={matter}
          onOpenTasks={() => {
            setIsTasksOpen(true);
          }}
          subItem={{
            id: vehicle.id,
            type: "vehicle",
          }}
        />
        <MatterSubItemNotesContextMenu
          matter={matter}
          onOpenNotes={() => {
            setIsNotesOpen(true);
          }}
          subItem={{
            id: vehicle.id,
            type: "vehicle",
          }}
        />
        <MatterSubItemFileReferencesContextMenu
          onOpenFileReferences={() => {
            setIsFileReferencesOpen(true);
          }}
          onCreateFileReferences={() => {
            setIsCreateFileReferenceOpen(true);
          }}
          onUploadFileReference={() => {
            fileReferenceUploadRef.current?.openFilePicker();
          }}
        />
        <MatterSubItemEventLogsContextMenu
          onOpenEventLogs={() => {
            setIsEventLogsOpen(true);
          }}
        />
      </ContextMenu>
      <VehicleCopyModeModal
        destinations={getRelatedMatterSelectionOptions(matter).filter(
          ({ id }) => pendingClientVehicleMatterIds.includes(id),
        )}
        open={pendingClientVehicleMatterIds.length > 0}
        onOpenChange={(open) => {
          if (!open) setPendingClientVehicleMatterIds([]);
        }}
        loading={copyPartyVehicle.isPending}
        onSubmit={(placements) => {
          copyPartyVehicle.mutate({
            vehicleId: vehicle.id,
            sourceMatterContactConnectionId: partyConnection.id,
            sourceMatterId: matter.id,
            placements,
          });
          setPendingClientVehicleMatterIds([]);
        }}
      />
      <ActionConfirmationModal
        open={isDeleteConfirmationOpen}
        setOpen={(open) => {
          if (!open && !deleteVehicle.isPending) {
            setIsDeleteConfirmationOpen(false);
          }
        }}
        title={`Are you sure you want to delete "${vehicleTitle}"?`}
        description="This permanently deletes the vehicle from every matter. This action cannot be undone."
        buttonText="Delete vehicle"
        afterActionFn={async () => {
          await deleteVehicle.mutateAsync({
            matterId: matter.id,
            id: vehicle.id,
          });
        }}
        isActionPending={deleteVehicle.isPending}
      />
      <ActionConfirmationModal
        open={isUnlinkConfirmationOpen}
        setOpen={(open) => {
          if (!open && !unlinkVehicle.isPending) {
            setIsUnlinkConfirmationOpen(false);
          }
        }}
        title={`Unlink "${vehicleTitle}" from this matter?`}
        description="This removes the vehicle from this matter only. If this is its last matter, the vehicle will be deleted."
        buttonText="Unlink vehicle"
        afterActionFn={async () => {
          await unlinkVehicle.mutateAsync({
            matterId: matter.id,
            objectId: vehicle.id,
            objectType: "vehicle",
          });
        }}
        isActionPending={unlinkVehicle.isPending}
      />
      <SelectContactModal
        matterId={matter.id}
        open={Boolean(contactSelection)}
        setOpen={(open) => {
          if (!open) setContactSelection(undefined);
        }}
        title={`Select ${contactSelection?.role ?? "contact"}`}
        excludeContactIds={
          contactSelection?.selectionMode === "multiple"
            ? contactSelection.connections.map(
                (connection) => connection.targetObjectId,
              )
            : undefined
        }
        defaultSearchObject={
          contactSelection?.role === "Auto Shop"
            ? { organizationSubTypes: ["Auto Shop"] }
            : undefined
        }
        onSelectContact={selectVehicleContact}
      />
      <EditContactModal
        activeContact={contactBeingEdited}
        activeContactId={contactBeingEdited?.id}
        hasEditPermission={canUpdateContact}
        open={Boolean(contactBeingEdited)}
        setOpen={(open) => {
          if (!open) setContactBeingEdited(undefined);
        }}
      />
      <MatterSubItemTasksModal
        open={isTasksOpen}
        onOpenChange={setIsTasksOpen}
        entityLabel={vehicleTitle}
        matter={matter}
        subItem={{
          id: vehicle.id,
          type: "vehicle",
        }}
      />
      <MatterSubItemNotesModal
        open={isNotesOpen}
        onOpenChange={setIsNotesOpen}
        entityLabel={vehicleTitle}
        matter={matter}
        subItem={{
          id: vehicle.id,
          type: "vehicle",
        }}
      />
      <MatterSubItemFileReferencesModal
        open={isFileReferencesOpen}
        onOpenChange={(open) => {
          setIsFileReferencesOpen(open);
          if (!open) {
            setIsCreateFileReferenceOpen(false);
          }
        }}
        createFileReferenceOpen={
          isFileReferencesOpen ? isCreateFileReferenceOpen : false
        }
        onCreateFileReferenceOpenChange={setIsCreateFileReferenceOpen}
        entityLabel={vehicleTitle}
        matter={matter}
        subItem={{
          id: vehicle.id,
          type: "vehicle",
        }}
      />
      <MatterSubItemEventLogsModal
        open={isEventLogsOpen}
        onOpenChange={setIsEventLogsOpen}
        entityLabel={vehicleTitle}
        matter={matter}
        subItem={{
          id: vehicle.id,
          type: "vehicle",
        }}
      />
      {isCreateFileReferenceOpen && !isFileReferencesOpen ? (
        <FileReferences
          matter={matter}
          subItem={{
            id: vehicle.id,
            type: "vehicle",
          }}
          createOnly
          createFileReferenceOpen
          onCreateFileReferenceOpenChange={setIsCreateFileReferenceOpen}
        />
      ) : null}
      <MatterSubItemFileReferenceUpload
        ref={fileReferenceUploadRef}
        matter={matter}
        subItem={{
          id: vehicle.id,
          type: "vehicle",
        }}
      />
    </>
  );
};
